import { supabase, friendlyError } from "../supabase.js";
import { workingDaysBetween } from "./calendar.js";

/* Baselines: what the plan said, kept so that drift can be measured rather
 * than remembered.
 *
 * Both WAPA files are named Rebaseline and neither contains a single baseline
 * field. Both were rebaselined by moving dates, which means the previous plan
 * exists only in the previous file and the question the document's own name
 * claims to answer cannot be answered from it.
 *
 * Rows are keyed on the permanent activity code, never on a row id or a WBS
 * number. A WBS renumbers the moment anything is inserted above it: comparing
 * the September file to August's that way reported twenty rows dropped and
 * twenty nine added, when most of those were the same activities that moved.
 */

const BATCH = 200;

/* Freeze the schedule as it stands now. */
export async function takeBaseline({ contractId, title, notes = null, userId = null, makeCurrent = true }) {
  if (!contractId) return { ok: false, error: "No contract is active." };
  if (!title?.trim()) return { ok: false, error: "Give the baseline a name." };

  const { data: rows, error: readFailed } = await supabase
    .from("schedule_stages")
    .select("activity_code, title, start_date, finish_date, duration_days, percent_complete")
    .eq("contract_id", contractId)
    .is("deleted_at", null);

  if (readFailed) return { ok: false, error: friendlyError(readFailed) };
  if (!rows?.length) return { ok: false, error: "There is no schedule to take a baseline of yet." };

  const usable = rows.filter((r) => r.activity_code);
  if (!usable.length) {
    return { ok: false, error: "These activities have no codes yet, so a baseline could not be matched back to them." };
  }

  /* Only one baseline is the one everything is measured against, and the
     database enforces that with a unique index, so the old one has to step
     aside before the new one claims it. */
  if (makeCurrent) {
    const { error } = await supabase
      .from("schedule_baselines")
      .update({ is_current: false, updated_by: userId })
      .eq("contract_id", contractId).eq("is_current", true);
    if (error) return { ok: false, error: friendlyError(error) };
  }

  const { data: baseline, error: madeFailed } = await supabase
    .from("schedule_baselines")
    .insert({
      contract_id: contractId, title: title.trim(), notes,
      is_current: makeCurrent, added_by: userId, updated_by: userId,
    })
    .select("id, title, taken_at").single();

  if (madeFailed) return { ok: false, error: friendlyError(madeFailed) };

  for (let i = 0; i < usable.length; i += BATCH) {
    const chunk = usable.slice(i, i + BATCH).map((r) => ({
      baseline_id: baseline.id,
      contract_id: contractId,
      activity_code: r.activity_code,
      title: r.title,
      start_date: r.start_date,
      finish_date: r.finish_date,
      duration_days: r.duration_days,
      percent_complete: r.percent_complete,
    }));
    const { error } = await supabase.from("schedule_baseline_rows").insert(chunk);
    if (error) {
      /* A half written baseline is worse than none: it would read as though
         the activities it never reached had not moved. */
      await supabase.from("schedule_baselines").delete().eq("id", baseline.id);
      return { ok: false, error: friendlyError(error) };
    }
  }

  return { ok: true, baseline, rows: usable.length };
}

/* Where the plan is now against where it said it would be.
 *
 * Pure, so it can be tested without a database and rendered without another
 * round trip. Slip is in working days and positive means later, which is the
 * direction everybody instinctively reads as bad. */
export function compareToBaseline(current = [], baselineRows = []) {
  const was = new Map(baselineRows.map((r) => [r.activity_code, r]));
  const seen = new Set();

  const rows = [];
  for (const now of current) {
    const before = was.get(now.activity_code);
    if (!before) {
      rows.push({ ...now, state: "added", startSlip: null, finishSlip: null });
      continue;
    }
    seen.add(now.activity_code);

    const slip = (from, to) => {
      if (!from || !to || from === to) return 0;
      const days = workingDaysBetween(from < to ? from : to, from < to ? to : from) - 1;
      return to > from ? days : -days;
    };

    rows.push({
      ...now,
      state: "kept",
      was: before,
      startSlip: slip(before.start_date, now.start_date),
      finishSlip: slip(before.finish_date, now.finish_date),
      progressChange: (now.percent_complete ?? 0) - (before.percent_complete ?? 0),
    });
  }

  /* An activity in the baseline that is no longer in the plan. Reported, not
     dropped: work disappearing between two versions of a schedule is the
     single most interesting thing a comparison can find. */
  for (const [code, before] of was) {
    if (!seen.has(code)) {
      rows.push({
        activity_code: code, title: before.title, state: "removed",
        was: before, startSlip: null, finishSlip: null,
      });
    }
  }

  const slipped = rows.filter((r) => (r.finishSlip ?? 0) > 0);
  const pulled = rows.filter((r) => (r.finishSlip ?? 0) < 0);

  return {
    rows,
    summary: {
      total: rows.length,
      kept: rows.filter((r) => r.state === "kept").length,
      added: rows.filter((r) => r.state === "added").length,
      removed: rows.filter((r) => r.state === "removed").length,
      slipped: slipped.length,
      pulled: pulled.length,
      worstSlip: slipped.reduce((worst, r) => Math.max(worst, r.finishSlip), 0),
      /* The average across everything that moved, which is a fairer read of
         a month than the worst row on its own. */
      averageSlip: slipped.length
        ? Math.round(slipped.reduce((sum, r) => sum + r.finishSlip, 0) / slipped.length)
        : 0,
    },
  };
}
